import {
  BedDouble,
  Sun,
  Scissors,
  GraduationCap,
  PartyPopper,
  Syringe,
} from "lucide-react";

export const IMAGES = {
  hero: "https://images.unsplash.com/photo-1503256207526-0d5d80fa2f47?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxoYXBweSUyMGRvZyUyMGRheSUyMGNhcmUlMjBib2FyZGluZyUyMGdyb29taW5nJTIwdHJhaW5pbmd8ZW58MHx8fHwxNzg5OTE4MTk4fDA&ixlib=rb-4.1.0&q=85",
  grooming: "https://images.unsplash.com/photo-1719464454959-9cf304ef4774?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGRvZyUyMGRheSUyMGNhcmUlMjBib2FyZGluZyUyMGdyb29taW5nJTIwdHJhaW5pbmd8ZW58MHx8fHwxNzg5OTE4MTk4fDA&ixlib=rb-4.1.0&q=85",
  playGroup: "https://images.pexels.com/photos/12983201/pexels-photo-12983201.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  portrait: "https://images.unsplash.com/photo-1561037404-61cd46aa615b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxwbGF5ZnVsJTIwZG9nJTIwcG9ydHJhaXQlMjBjcmVhbSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzg5OTE4MTk4fDA&ixlib=rb-4.1.0&q=85",
  pomeranian: "https://images.pexels.com/photos/15096715/pexels-photo-15096715.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  meal: "https://images.pexels.com/photos/30070537/pexels-photo-30070537.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  bowl: "https://images.unsplash.com/photo-1714068691210-073dc52c6c1d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxkb2clMjBmcmVzaCUyMGhlYWx0aHklMjBjb29rZWQlMjBtZWFsJTIwZm9vZCUyMGJvd2x8ZW58MHx8fHwxNzg5OTE4MjAyfDA&ixlib=rb-4.1.0&q=85",
};

export const SERVICES = [
  {
    icon: BedDouble,
    name: "Boarding",
    tag: "Overnight luxury stays",
    blurb:
      "Calm, climate-comfortable suites with freshly cooked meals, evening wind-downs and breakout zones matched to your pup's temperament.",
    detail:
      "When your pup stays over, they get a home — not a kennel. Our boarding suites sit on specialised flooring chosen with pups' wellbeing in mind: gentle on joints, kind to paws and effortless to keep hygienic. Days are structured with supervised play, socialisation and rest, and nights are quiet and monitored. Every guest enjoys 2–3 freshly cooked, preservative-free meat-based meals depending on their age, and our trained staff keep a daily log you can review from the customer portal.",
  },
  {
    icon: Sun,
    name: "Day Care",
    tag: "Play, socialise, rest",
    blurb:
      "A full day of supervised play, socialisation and structured rest cycles — so your pup comes home happy, not hyper.",
    detail:
      "Our day care runs on a rhythm designed by trainers: high-energy supervised play sessions, socialisation groups matched by size and temperament, and proper nap windows in between. Multiple breakout areas mean the shy ones, the seniors and the social butterflies each get a space that suits them. Treats and snacks turn up through the day as part of enrichment activities.",
  },
  {
    icon: Scissors,
    name: "Grooming",
    tag: "Spa & hygiene rituals",
    blurb:
      "Baths, de-shedding, nail care and breed-specific styling — always with pet-safe, vet-approved products.",
    detail:
      "Grooming at WhatTheBark is a spa ritual, not a chore. We use only pet-safe, vet-approved shampoos and products, and every session includes a basic hygiene check. From a quick tidy-up to a full breed-specific groom, our groomers work patiently so even first-timers leave wagging.",
  },
  {
    icon: GraduationCap,
    name: "Training",
    tag: "Positive reinforcement",
    blurb:
      "India's top trainers, positive reinforcement only — from puppy basics to behaviour correction, with treats built into every session.",
    detail:
      "Our training programs are led by some of the country's top trainers, and our floor staff are trained by them too — so good manners are reinforced all day, not just in class. We use positive reinforcement with treats and enrichment games, covering puppy foundations, obedience, socialisation and behaviour correction.",
  },
  {
    icon: PartyPopper,
    name: "Events",
    tag: "Celebrations & meet-ups",
    blurb:
      "Birthday bashes, breed meet-ups, agility days and festive celebrations — safe, supervised and unforgettable.",
    detail:
      "From pup birthday parties with dog-safe cakes to breed meet-ups, agility runs and festive celebrations, our events calendar keeps the pack buzzing. Every event is fully supervised by our trained handlers, with breakout zones ready for pups who need a breather from the party.",
  },
  {
    icon: Syringe,
    name: "Vaccinations",
    tag: "Vet care on schedule",
    blurb:
      "Regular vet visits, on-site vaccination drives and thorough health checkups — records synced to your pup's profile.",
    detail:
      "A vet visits our facility regularly to oversee hygiene and conduct thorough checkups. We run on-site vaccination drives so your pup's immunisation stays on schedule, and every record is maintained against your pup's profile — visible in the customer portal when it launches.",
  },
];

export const MANIFESTO = [
  {
    num: "01",
    title: "Floors that love their paws back",
    text: "Our facility is built while keeping pups and their needs in mind — starting from the ground up. The specialised flooring is gentle on joints, kind to paws and seamless, so hygiene stays effortless.",
  },
  {
    num: "02",
    title: "Trained staff, regular vet visits",
    text: "A vet visits regularly for hygiene oversight and thorough checkups, and every handler on the floor is trained to understand dogs — not just supervise them.",
  },
  {
    num: "03",
    title: "Pet-safe products, top-notch hygiene",
    text: "Everything that touches your pup — from floor cleaners to shampoos — is 100% pet safe, and every play zone is sanitised through the day.",
  },
  {
    num: "04",
    title: "Breakout areas for every temperament",
    text: "Shy, senior or super-social — multiple breakout areas let us group pups by temperament, size and energy, so each dog gets a space to truly relax.",
  },
  {
    num: "05",
    title: "Top trainers shape our whole team",
    text: "We work with some of India's top trainers — for your pups and for our staff — so positive reinforcement runs through every interaction.",
  },
];

export const STATS = [
  { value: "10,000+", label: "Happy pups cared for" },
  { value: "4.9", label: "Google rating" },
  { value: "24/7", label: "Supervised care" },
  { value: "2–3", label: "Fresh meals daily" },
];

export const MARQUEE_ITEMS = [
  "SPECIALISED FLOORING FOR PUP WELLBEING",
  "REGULAR VET VISITS & CHECKUPS",
  "NO-PRESERVATIVE FRESH MEALS",
  "TEMPERAMENT-BASED BREAKOUT ZONES",
  "SUPERVISED PLAY & SOCIALISATION",
  "PET-SAFE PRODUCTS ONLY",
];

export const SOCIALS = [
  { name: "Facebook", href: "#" },
  { name: "Instagram", href: "#" },
  { name: "LinkedIn", href: "#" },
  { name: "YouTube", href: "#" },
];
